import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  login as apiLogin,
  signup as apiSignup,
  logout as apiLogout,
  getMe,
  tokenStore,
} from '../api';

/**
 * AuthContext — single source of truth for "is anyone logged in, and who".
 *
 * While MarginPro is in development, the entire app sits behind this
 * context: App.jsx wraps every route in <ProtectedRoute>, which reads
 * `isAuthenticated`/`loading` from here to decide whether to render the
 * page or bounce to /login. See components/ProtectedRoute.jsx.
 */

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // true while we validate any stored token

  // On first load, if a token is already stored, confirm it's still valid
  // and hydrate the user object. Otherwise, nothing to check.
  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      if (!tokenStore.get()) {
        setLoading(false);
        return;
      }
      try {
        const data = await getMe();
        if (!cancelled) setUser(data.data?.user || null);
      } catch {
        // Expired/invalid token — clear it silently.
        tokenStore.clear();
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    bootstrap();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (credentials) => {
    const data = await apiLogin(credentials);
    setUser(data.data?.user || data.user || null);
    return data;
  }, []);

  const signup = useCallback(async (payload) => {
    const data = await apiSignup(payload);
    setUser(data.data?.user || data.user || null);
    return data;
  }, []);

  const logout = useCallback(() => {
    apiLogout();
    setUser(null);
  }, []);

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    signup,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

export default AuthContext;
